import { Stethoscope, HeartPulse, Sparkles, Coffee, BadgeCheck, Wallet, ArrowRight, Phone } from "lucide-react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import { COMPANY, IMAGES } from "../lib/constants";

const services = [
  {
    id: "skilled-nursing",
    icon: Stethoscope,
    title: "Skilled Nursing",
    img: IMAGES.skilledNursing,
    intro:
      "RN-led clinical care delivered in your home — without the hospital stay. We work closely with your physician to follow your care plan precisely.",
    bullets: [
      "Medication management & administration",
      "Wound care & post-operative monitoring",
      "Chronic disease care (diabetes, CHF, COPD)",
      "Post-hospital transitional care",
      "IV therapy & injections (as prescribed)",
    ],
  },
  {
    id: "private-duty-nursing",
    icon: HeartPulse,
    title: "Private Duty Nursing",
    img: IMAGES.heroNurse,
    intro:
      "One-on-one nursing for complex, ongoing needs — for both pediatric and adult patients. Available around the clock when you need it most.",
    bullets: [
      "24/7 continuous nursing care",
      "Ventilator & tracheostomy management",
      "Pediatric & adult specialty care",
      "Feeding tube & complex equipment support",
      "Care coordination with your medical team",
    ],
  },
  {
    id: "personal-care",
    icon: Sparkles,
    title: "Personal Care",
    img: IMAGES.personalCare,
    intro:
      "Daily living support delivered with warmth and dignity — so your loved one can stay independent and comfortable at home.",
    bullets: [
      "Bathing, grooming, and dressing",
      "Meal preparation and feeding assistance",
      "Mobility & transfer assistance",
      "Light housekeeping & laundry",
      "Companionship & emotional support",
    ],
  },
  {
    id: "respite-care",
    icon: Coffee,
    title: "Respite Care",
    img: IMAGES.respiteCare,
    intro:
      "Short-term caregiver relief, on your schedule. Take a break, attend to errands, or simply rest — we'll be there.",
    bullets: [
      "Short-term scheduled relief",
      "Hourly, overnight, or weekend coverage",
      "Flexible, on-demand scheduling",
      "Same caregiver continuity when possible",
      "Peace of mind for family caregivers",
    ],
  },
];

export default function Services() {
  return (
    <>
      <SEO
        title="Home Care Services in Springfield, Fairfax & Northern Virginia | 1st Home Care"
        description="Skilled nursing, private duty nursing, personal care, and respite care services in Northern Virginia. Medicaid accepted. Private pay available. Call 703-981-8201."
        keywords="skilled nursing at home VA, private duty nursing Northern Virginia, personal care Fairfax, respite care Springfield VA, home health services"
        canonical="https://1sthomecare.com/services"
      />

      <section className="container-pad section-pad">
        <div className="max-w-3xl">
          <div className="text-xs font-bold uppercase tracking-widest text-brand-amber mb-3">
            Our Services
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-brand-blue-dark text-balance">
            Comprehensive home care, designed around your family.
          </h1>
          <p className="mt-6 text-lg text-gray-600 leading-relaxed">
            Whether you need skilled clinical care or daily living support, our
            licensed nurses and trained caregivers bring the right level of help
            home — and adjust as your needs change.
          </p>
        </div>
      </section>

      {services.map((s, i) => {
        const hideImage = s.id === "skilled-nursing";
        return (
          <section
            key={s.id}
            id={s.id}
            data-testid={`service-section-${s.id}`}
            className={`section-pad ${i % 2 === 1 ? "bg-brand-gray/40" : ""}`}
          >
            <div
              className={`container-pad grid gap-12 items-center ${
                hideImage ? "lg:grid-cols-1" : "lg:grid-cols-2"
              }`}
            >
              <div className={!hideImage && i % 2 === 1 ? "lg:order-2" : ""}>
                <div className="w-12 h-12 rounded-xl bg-brand-blue/10 text-brand-blue flex items-center justify-center mb-5">
                  <s.icon className="w-6 h-6" />
                </div>
                <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-brand-blue-dark">
                  {s.title}
                </h2>
                <p className="mt-4 text-gray-600 leading-relaxed">{s.intro}</p>
                <ul className={`mt-6 grid gap-3 ${hideImage ? "sm:grid-cols-2 lg:grid-cols-3" : "sm:grid-cols-2"}`}>
                  {s.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2">
                      <BadgeCheck className="w-4 h-4 text-brand-amber mt-0.5 shrink-0" />
                      <span className="text-gray-700 text-sm">{b}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-8 flex flex-wrap gap-3">
                  <Link
                    to="/contact"
                    data-testid={`service-cta-contact-${s.id}`}
                    className="inline-flex items-center gap-2 bg-brand-blue hover:bg-brand-blue-dark text-white font-semibold px-6 py-3 rounded-full"
                  >
                    Request {s.title} <ArrowRight className="w-4 h-4" />
                  </Link>
                  <a
                    href={`tel:${COMPANY.phoneTel}`}
                    data-testid={`service-cta-call-${s.id}`}
                    className="inline-flex items-center gap-2 border border-gray-300 hover:border-brand-blue text-brand-blue-dark font-semibold px-6 py-3 rounded-full"
                  >
                    <Phone className="w-4 h-4" /> {COMPANY.phone}
                  </a>
                </div>
              </div>

              {!hideImage && (
                <div className={`relative ${i % 2 === 1 ? "lg:order-1" : ""}`}>
                  <img
                    src={s.img}
                    alt={s.title}
                    className="w-full h-[440px] object-cover rounded-3xl shadow-xl"
                  />
                </div>
              )}
            </div>
          </section>
        );
      })}

      {/* PAYMENT */}
      <section data-testid="payment-section" className="container-pad section-pad">
        <div className="bg-gradient-to-br from-brand-blue to-brand-blue-dark text-white rounded-3xl p-8 md:p-12 shadow-xl">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div>
              <div className="text-xs font-bold uppercase tracking-widest text-brand-amber-light mb-3">
                Payment Options
              </div>
              <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight">
                Flexible payment options for every family.
              </h2>
              <p className="mt-4 text-white/85 leading-relaxed">
                We accept Medicaid and offer private pay, so you can focus on
                care — not the paperwork. Our team helps you understand your
                coverage and get started quickly.
              </p>
            </div>
            <div className="grid gap-4">
              <div className="bg-white/10 backdrop-blur rounded-2xl p-6 border border-white/15">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-brand-amber text-white flex items-center justify-center">
                    <BadgeCheck className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="font-heading text-xl font-semibold">Medicaid Accepted</div>
                    <div className="text-sm text-white/80">Approved Virginia Medicaid provider.</div>
                  </div>
                </div>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-2xl p-6 border border-white/15">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-brand-amber text-white flex items-center justify-center">
                    <Wallet className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="font-heading text-xl font-semibold">Private Pay Available</div>
                    <div className="text-sm text-white/80">Flexible plans for every household.</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
